import { useEffect } from "react";
import type { AbstractAgent } from "@ag-ui/client";
import { useSWRConfig } from "swr";
import { projectDatasetsPath } from "~/services/projects";

/**
 * Re-validate the project's attached dataset ids when an agent run ends, so a
 * dataset an agent creates and attaches to the project server-side (writes SWR
 * can't observe) shows up in the nextgen `SessionContextSidebar` without a manual
 * refresh. A new id changes the key `useDatasetsById` fetches the records under,
 * so refreshing the id list is enough.
 *
 * Keyed off the terminal run event (rather than `onToolCallEndEvent`) on purpose:
 * a tool like `create_dataset` provisions a schema and attaches mid-turn, well
 * after the tool-call args finish streaming, so only the run's end guarantees the
 * writes have landed — and it keeps this decoupled from the tool's name.
 */
export function useProjectDatasetSync(agent: AbstractAgent, projectId: string | undefined) {
  const { mutate } = useSWRConfig();
  useEffect(() => {
    if (!projectId) return;
    const revalidate = async () => {
      try {
        await mutate(projectDatasetsPath(projectId));
      } catch (err) {
        console.error("[useProjectDatasetSync] Failed to refresh project datasets:", err);
      }
    };
    const onRunEnd = () => void revalidate();
    const sub = agent.subscribe({
      onRunFinishedEvent: onRunEnd,
      onRunErrorEvent: onRunEnd,
    });
    return () => sub.unsubscribe();
  }, [agent, projectId, mutate]);
}
